/**
 * Body Armor Protection Constants
 * PSP/CRE Tool v3.0
 *
 * VERSION: 1.0.0 - Initial implementation (March 10, 2026)
 *   ENH-BAP: Body armor KIA reduction modifier, Category 20 of Research Inventory Audit.
 *   Implements NIJ/NATO protection level taxonomy with per-level KIA reduction factors.
 *
 * DOCTRINAL BASIS:
 *   - ATP 4-02.2 §3-14: Personal protective equipment planning factors
 *   - AJMedP-1 Annex C: Casualty estimation adjustments for force protection
 *   - STANAG 4188: Individual ballistic protection standards
 *   - Ukraine 2022-2024 analysis: Modern plate carriers show 15-20% KIA reduction
 *     in peer-adversary artillery/fragment engagements (Institute for the Study of War,
 *     Johns Hopkins APL combat casualty data synthesis)
 *   - USMC Studies and Analysis Division (2019): Level III/IV SAPI plates reduce
 *     torso penetrations by ~40%, translating to ~15-18% overall KIA reduction
 *     when accounting for non-torso mechanisms (head wounds, extremity hemorrhage).
 *
 * IMPLEMENTATION NOTE:
 *   kia_reduction is applied AFTER mechanism and TCCC modifiers as a multiplicative
 *   factor: kiaRate *= (1 - kia_reduction). It represents the reduction in the
 *   fraction of WIA/KIA that die from torso penetrations that armor intercepts.
 *   Head wounds, limb amputations, and blast effects are NOT reduced by this factor.
 *
 * DEFAULT: LEVEL_III — NATO standard issue, Romanian Army, most NATO member forces.
 *   Level IV (ESAPI/XSAPI) is US Army/USMC standard. Level IIIA is common in
 *   gendarmerie/police units deployed to support military operations.
 */

// ============================================================================
// BODY ARMOR PROTECTION LEVELS
// ============================================================================

export const BODY_ARMOR_LEVELS = {

  NONE: {
    key: 'NONE',
    label: 'No Armor',
    short_label: 'None',
    nij_class: 'N/A',
    kia_reduction: 0.00,    // 0% — no protection
    description: 'Unprotected forces. Historical/irregular warfare baseline.',
    examples: 'Pre-modern forces, some irregular/militia units',
    doctrinal_note: 'Not applicable to NATO/Romanian Army conventional operations',
    color: 'error'
  },

  LEVEL_IIA: {
    key: 'LEVEL_IIA',
    label: 'Level IIA (Soft Armor)',
    short_label: 'IIA',
    nij_class: 'NIJ Level IIA',
    kia_reduction: 0.05,    // 5% — soft armor, pistol rounds, fragmentation
    description: 'Soft body armor. Handgun and fragmentation protection only. No rifle plate.',
    examples: 'Logistics/support units, some gendarmerie configurations',
    doctrinal_note: 'Insufficient for conventional peer-adversary fire environment',
    color: 'warning'
  },

  LEVEL_IIIA: {
    key: 'LEVEL_IIIA',
    label: 'Level IIIA (Enhanced Soft Armor)',
    short_label: 'IIIA',
    nij_class: 'NIJ Level IIIA',
    kia_reduction: 0.08,    // 8% — submachine gun/pistol + improved frag
    description: 'Enhanced soft armor. Pistol + .44 Magnum protection. No rifle plate.',
    examples: 'Rear-echelon units, staff, some Allied nations',
    doctrinal_note: 'Marginal for conventional combat operations',
    color: 'warning'
  },

  LEVEL_III: {
    key: 'LEVEL_III',
    label: 'Level III (Hard Plate)',
    short_label: 'III',
    nij_class: 'NIJ Level III / STANAG 4188 Class IV',
    kia_reduction: 0.15,    // 15% — rifle plates (7.62×51 M80 ball)
    description: 'Hard ballistic plate. Protects against rifle calibers up to 7.62×51 NATO. Standard NATO issue.',
    examples: 'Romanian Army (current issue), most NATO member forces, standard plate carrier',
    doctrinal_note: 'NATO baseline standard. Default for most conventional force calculations.',
    color: 'success'
  },

  LEVEL_IV: {
    key: 'LEVEL_IV',
    label: 'Level IV (Armor-Piercing)',
    short_label: 'IV',
    nij_class: 'NIJ Level IV / ESAPI',
    kia_reduction: 0.20,    // 20% — AP rifle rounds (7.62×63 M2 AP)
    description: 'Armor-piercing protection. ESAPI/XSAPI plates. Highest protection level.',
    examples: 'US Army (ESAPI/XSAPI standard issue), USMC, some allied SOF units',
    doctrinal_note: 'Heaviest standard-issue protection. Increased metabolic cost accepted for AP threat environments.',
    color: 'success'
  }

};

// ============================================================================
// DEFAULTS AND HELPERS
// ============================================================================

/** Default armor level for NATO conventional forces */
export const DEFAULT_BODY_ARMOR_LEVEL = 'LEVEL_III';

/**
 * Get the KIA reduction factor for a given armor level.
 * Returns 0 (no reduction) if level is unknown — fail-safe, never inflates.
 * @param {string} armorLevel - Key from BODY_ARMOR_LEVELS
 * @returns {number} kia_reduction (0.0 – 0.20)
 */
export function getArmorKIAReduction(armorLevel) {
  return BODY_ARMOR_LEVELS[armorLevel]?.kia_reduction ?? 0;
}

/**
 * Validate an armor level key.
 * @param {string} armorLevel
 * @returns {boolean}
 */
export function isValidArmorLevel(armorLevel) {
  return armorLevel in BODY_ARMOR_LEVELS;
}

// ============================================================================
// ARMOR EFFECT ON THE WOUND MIX (2026-10-11, Dr. Tatar; docs/research/RESEARCH_ARMOR_EQUIPMENT.md, docs/MASTER_TODO.md)
// ============================================================================
// Body armor does not change the NUMBER of casualties; it changes the killed share (kia_reduction above) and the TYPE of
// wounds: fewer thorax wounds. The wound distribution tables (woundTypeDistribution.js) are read as the mix WITHOUT modern
// armor; the armor level scales the thorax share and the other regions are renormalized proportionally (total unchanged).
//
//   thorax share  x  (1 - w x (1 - THORAX_SHARE_RATIO))        w = protection weight of the armor level (0 to 1)
//
// THORAX_SHARE_RATIO = 6 / 13: thorax 13% of wounds in World War II and Vietnam, 6% in OEF/OIF (Owens et al. 2008,
// Military Medicine abstract). It is an ERA comparison (armor together with other changes), used only as a ratio because
// Owens counts wounds while the tables are shares of wounded casualties.
// The weight is kia_reduction / kia_reduction(LEVEL_III), capped at 1: NONE 0, IIA 0.33, IIIA 0.53, III 1, IV 1
// (Level IV stops armor-piercing rifle rounds; the model has no ammunition input, so it is treated as Level III here).
// The kia_reduction values behind the weights are themselves unsourced: the weights of IIA and IIIA are educated guesses.
export const ARMOR_WOUND_MIX = {
  thorax_share_ratio: 6 / 13,
  status: 'SOURCED_ERA_COMPARISON',
  source: 'Owens et al. 2008 (OIF/OEF combat wounds): thorax 6% versus 13% in WWII and Vietnam. Weights of IIA and IIIA: placeholder.'
};

/**
 * Protection weight of an armor level for the wound mix (0 = none, 1 = modern plate carrier).
 * Throws on an unknown level (the killed-share helper above returns 0; this one must not hide a bad key).
 */
export function getArmorProtectionWeight(armorLevel) {
  const level = BODY_ARMOR_LEVELS[armorLevel];
  if (!level) {
    throw new Error(`bodyArmorProtection.getArmorProtectionWeight: unknown armor level '${armorLevel}' (valid: ${Object.keys(BODY_ARMOR_LEVELS).join(', ')})`);
  }
  return Math.min(1, level.kia_reduction / BODY_ARMOR_LEVELS.LEVEL_III.kia_reduction);
}

const MIX_REGIONS = ['extremity', 'thorax', 'abdomen', 'head_neck', 'burns', 'multi'];

/**
 * Wound distribution adjusted for body armor: fewer thorax wounds, other regions scaled proportionally, shares sum to 1.
 * @param {Object} distribution shares by region (extremity, thorax, abdomen, head_neck, burns, multi)
 * @param {string} armorLevel key of BODY_ARMOR_LEVELS
 * @returns {Object} new distribution (the input is not modified)
 */
export function applyArmorToWoundMix(distribution, armorLevel) {
  if (!distribution) throw new Error('bodyArmorProtection.applyArmorToWoundMix: distribution is required');
  const w = getArmorProtectionWeight(armorLevel);
  const adjusted = {};
  let total = 0;
  MIX_REGIONS.forEach((r) => {
    const share = distribution[r];
    if (typeof share !== 'number' || Number.isNaN(share) || share < 0) {
      throw new Error(`bodyArmorProtection.applyArmorToWoundMix: distribution.${r} must be a number >= 0 (got: ${share})`);
    }
    adjusted[r] = r === 'thorax' ? share * (1 - w * (1 - ARMOR_WOUND_MIX.thorax_share_ratio)) : share;
    total += adjusted[r];
  });
  const result = { ...distribution };
  MIX_REGIONS.forEach((r) => { result[r] = adjusted[r] / total; });
  return result;
}
